// import { CheckBox } from '@mui/icons-material';
import CommentIcon from '@mui/icons-material/Comment';
import {
  Box, Checkbox, Divider,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import postUpdateGroup from '../../../api/private/users_management/posts/postUpdateGroup';
import Modal from '../../../components/Modal';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#dadde9',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

function EditGroupModal({
  open,
  handleClose,
  selectedGroup,
  updatedGroup,
  setUpdatedGroup,
  userList,
}) {
  const theme = useTheme();
  const [groupName, setGroupName] = React.useState(selectedGroup?.GroupName);
  const sites = useSelector((state) => state.fascias);
  const userWhichLoggedIn = useSelector((state) => state.user.userProfile);
  const users = userList.filter((user) => user.GroupId === selectedGroup.Id);
  const flatFasciaList = sites.flatMap((obj) => obj.Sites);

  const defaultFaciaOptions = flatFasciaList
    .filter((obj) => selectedGroup.SiteIds.includes(obj.SiteId))
    .map((obj) => ({ label: obj.Name, value: obj.SiteId }));

  const [selectedOptions, setSelectedOptions] = React.useState(defaultFaciaOptions);
  const [siteIds, setSitesIds] = React.useState(defaultFaciaOptions.map((option) => option.value));
  const [isFasciasFocused, setIsFasciasFocused] = React.useState(false);
  const [isGroupNameFocused, setIsGroupNameFocused] = React.useState(false);

  const disableSave = userWhichLoggedIn.user_permission !== 'Admin';
  const allSites = sites.flatMap((obj) => obj.Sites)
    .map((obj) => ({ label: obj.Name, value: obj.SiteId }));

  const customOptions = [{ label: 'Select All', value: 'all' }, ...allSites];
  const isFasciasActive = selectedOptions.length > 0 || isFasciasFocused;
  const isGroupNameActive = groupName?.length > 0 || isGroupNameFocused;
  const allSelected = selectedOptions?.length === allSites?.length;
  const isIndeterminate = selectedOptions?.length > 0 && !allSelected;

  const updateGroup = async (id, name, sitesInGroup) => {
    await postUpdateGroup(id, name, sitesInGroup)
      .then(() => {
        handleClose(false);
        toast.success('Successfully updated group');
        setUpdatedGroup(() => !updatedGroup);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleFaciaChange = (event, values) => {
    const isSelectAllSelected = values.some((option) => option.label === 'Select All');
    const allOptionsSelected = customOptions.filter((option) => option.label !== 'Select All');

    if (isSelectAllSelected) {
      if (selectedOptions.length === allOptionsSelected.length) {
        setSelectedOptions([]);
        setSitesIds([]);
      } else {
        setSelectedOptions(allOptionsSelected);
        setSitesIds(allOptionsSelected.map((option) => option.value));
      }
    } else {
      const uniqueValues = values.filter(
        (option, index, self) => option.label !== 'Select All'
          && index === self.findIndex((o) => o.value === option.value),
      );
      setSelectedOptions(uniqueValues);
      setSitesIds(uniqueValues.map((option) => option.value));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${selectedGroup.GroupName}`}
      disableButton={disableSave}
      titleVariant="h4"
      desktopModalWidth="60%"
      desktopModalHeight="60vh"
      buttonLabel="SAVE"
      buttonOnClick={() => updateGroup(selectedGroup.Id, groupName, siteIds)}
    >
      <Box display="flex" gap={1}>
        <Stack spacing={1} width={400}>
          <Typography variant="h5">Group Details</Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                position: 'absolute',
                top: isGroupNameActive ? 0 : '50%',
                left: 10,
                zIndex: 1,
                backgroundColor: 'white',
                padding: '0 4px',
                transform: isGroupNameActive ? 'translateY(-50%)' : 'translateY(-50%)',
                transition: 'top 0.2s ease-in-out, font-size 0.2s ease-in-out',
                color: isGroupNameActive ? theme.palette.text.secondary : theme.palette.text.primary,
              }}
            >
              GroupName*
            </Typography>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px 8px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TextField
                required
                fullWidth
                variant="standard"
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                onFocus={() => setIsGroupNameFocused(true)}
                onBlur={() => setIsGroupNameFocused(false)}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  '& .MuiInput-root:before, & .MuiInput-root:after': {
                    borderBottom: 'none !important',
                  },
                }}
              />
            </Box>
          </Box>
          <Stack spacing={1}>
            <Typography variant="h5">Access</Typography>
            <Box
              sx={{
                position: 'relative',
                minWidth: 200,
                maxWidth: 400,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  position: 'absolute',
                  top: isFasciasActive ? 0 : '50%',
                  left: 10,
                  zIndex: 1,
                  backgroundColor: 'white',
                  padding: '0 4px',
                  transform: isFasciasActive ? 'translateY(-50%)' : 'translateY(-50%)',
                  transition: 'top 0.2s ease-in-out, font-size 0.2s ease-in-out',
                  color: isFasciasActive ? theme.palette.text.secondary : theme.palette.text.primary,
                }}
              >
                Fascias*
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '16px 8px 8px 8px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Autocomplete
                  multiple
                  id="fascias-autocomplete"
                  options={customOptions}
                  value={selectedOptions}
                  onChange={handleFaciaChange}
                  onFocus={() => setIsFasciasFocused(true)}
                  onBlur={() => setIsFasciasFocused(false)}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.value}>
                      <Checkbox
                        checked={
                          option.label === 'Select All'
                            ? allSelected
                            : selected
                        }
                        indeterminate={option.label === 'Select All' && isIndeterminate}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="standard"
                      label=""
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      sx={{
                        '& .MuiInput-root:before, & .MuiInput-root:after': {
                          borderBottom: 'none !important',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack>
          <Typography variant="h5">Users</Typography>
          <Box>
            <Stack>
              <List sx={{
                maxHeight: 500, overflow: 'auto',
              }}
              >
                {users.map((user) => (
                  <ListItem
                    key={user.Id}
                    disableGutters
                    secondaryAction={(
                      <IconButton aria-label="Fascias">
                        <HtmlTooltip
                          sx={{ maxHeight: 200, overflow: 'auto' }}
                          title={(
                            <>
                              <Typography color="inherit">
                                {user.AccountName}
                                {' '}
                                Access:
                              </Typography>
                              <ul type="1">
                                {
                                  flatFasciaList
                                    .filter((site) => user.SiteIds.includes(site.SiteId))
                                    .map((obj) => (
                                      <li>{obj.Name}</li>
                                    ))
                                }
                              </ul>
                            </>
                          )}
                        >
                          <CommentIcon />
                        </HtmlTooltip>
                      </IconButton>
                    )}
                    sx={{
                      borderBottom: '1px solid',
                    }}
                  >
                    <ListItemText
                      primary={`${user.UserName}`}
                      sx={{
                        ml: 1,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}

export default EditGroupModal;
